import {
  AFFILIATION_TYPE_LABELS,
  AUDIENCE_BUCKET_LABELS,
  DISCREDITATION_TYPE_LABELS,
  INAUTHENTIC_TYPE_LABELS,
  NodeFilterCategory,
  NodeFilterOption,
  NodeFilters,
  RoleGroup,
  SCOPE_FILTER_LABELS,
  ZSCORE_BUCKET_LABELS,
  ZScoreBucket,
  ZScoreGroup,
} from './graphTypes';
import { mentionTypeLabel } from './timelineUtils';
import { platformNamesMapping } from '../../utils/platforms';

// Translator shape shared by the page and the panel — i18next's TFunction is
// generic over its return type, so the builders take the narrow callable
export type Translate = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export interface NodeFilterSectionOption {
  value: string;
  count: number;
  label: string;
}

export interface NodeFilterSection {
  key: NodeFilterCategory;
  // i18n key of the section title (translated by the consumer)
  title: string;
  options: NodeFilterSectionOption[];
}

export interface NodeFilterOptions {
  scopes: NodeFilterOption[];
  communities: NodeFilterOption[];
  tags: NodeFilterOption[];
  affiliationTypes: NodeFilterOption[];
  affiliationCountries: NodeFilterOption[];
  discreditations: NodeFilterOption[];
  inauthenticTypes: NodeFilterOption[];
  originCountries: NodeFilterOption[];
  audienceBuckets: NodeFilterOption[];
}

// Node facet sections of the Filters tab (Actors sub-tab), in the Figma order
// (1231-526). Only sections with values are returned — graphs generated before
// backend PR #823 carry no affiliation/inauthentic metadata, mention graphs
// carry no origin/audience profile fields. Categories where EVERY actor has a
// value (platform, role, z-score bucket) need at least two values to be worth
// narrowing by; sparse categories filter usefully even with a single value
// (checked = only actors carrying it). Built once on the page and shared with
// the canvas filter chips so both read the same labels.
export function buildNodeFilterSections({
  t,
  platforms,
  roleGroups,
  zscoreGroups,
  options,
}: {
  t: Translate;
  platforms: { name: string; count: number }[];
  // already stripped of the UI-hidden role categories
  roleGroups: RoleGroup[];
  zscoreGroups: ZScoreGroup[];
  options: NodeFilterOptions;
}): NodeFilterSection[] {
  const withLabels = (
    list: NodeFilterOption[],
    labels?: Record<string, string>,
    translate = true,
  ): NodeFilterSectionOption[] =>
    list.map(({ value, count }) => {
      const raw = labels?.[value] ?? value;
      return { value, count, label: translate ? t(raw) : raw };
    });
  const sections: (NodeFilterSection & { minOptions: number })[] = [
    {
      key: 'platforms',
      title: 'Platforms',
      minOptions: 2,
      options: platforms.map(({ name, count }) => ({
        value: name,
        count,
        label: platformNamesMapping[name] ?? name,
      })),
    },
    {
      key: 'roles',
      title: 'Actor roles',
      minOptions: 2,
      options: roleGroups.map(({ role, members }) => ({
        value: role,
        count: members.length,
        label: t(role),
      })),
    },
    {
      key: 'zscoreBuckets',
      title: 'Z-Score anomaly',
      minOptions: 2,
      options: zscoreGroups.map(({ bucket, members }) => ({
        value: bucket,
        count: members.length,
        label: t(ZSCORE_BUCKET_LABELS[bucket]),
      })),
    },
    {
      key: 'scopes',
      title: 'Actor group',
      minOptions: 2,
      options: withLabels(options.scopes, SCOPE_FILTER_LABELS),
    },
    {
      key: 'discreditations',
      title: 'High risk accounts',
      minOptions: 1,
      options: withLabels(options.discreditations, DISCREDITATION_TYPE_LABELS),
    },
    {
      key: 'inauthenticTypes',
      title: 'Inauthentic behavior',
      minOptions: 1,
      options: withLabels(options.inauthenticTypes, INAUTHENTIC_TYPE_LABELS),
    },
    {
      key: 'affiliationTypes',
      title: 'State affiliated',
      minOptions: 1,
      options: withLabels(options.affiliationTypes, AFFILIATION_TYPE_LABELS),
    },
    {
      key: 'affiliationCountries',
      title: 'Affiliation country',
      minOptions: 1,
      // country names come from the backend as display strings — no label map
      options: withLabels(options.affiliationCountries, undefined, false),
    },
    {
      key: 'communities',
      title: 'Communities',
      minOptions: 2,
      options: options.communities.map(({ value, count }) => ({
        value,
        count,
        label: t('Community #{{id}}', { id: value }),
      })),
    },
    {
      key: 'originCountries',
      title: 'Origin country',
      minOptions: 1,
      options: withLabels(options.originCountries, undefined, false),
    },
    {
      key: 'audienceBuckets',
      title: 'Audience',
      minOptions: 1,
      // bucket labels are numeric ranges — nothing to translate
      options: withLabels(
        options.audienceBuckets,
        AUDIENCE_BUCKET_LABELS,
        false,
      ),
    },
    {
      key: 'tags',
      title: 'Tags',
      minOptions: 1,
      // tag display names travel with the data (localized in the options memo)
      options: options.tags.map((option) => ({
        value: option.value,
        count: option.count,
        label: option.label ?? option.value,
      })),
    },
  ];
  return sections
    .filter((section) => section.options.length >= section.minOptions)
    .map(({ minOptions: _min, ...section }) => section);
}

// One chip per active filter TYPE in the canvas row (Figma 1229-52): "Group:
// first value +N" for value filters, label only for on/off ones. Removing a chip
// resets the whole type — the page maps `id` back onto its state.
export type FilterChipId =
  | { kind: 'facet'; category: NodeFilterCategory }
  | { kind: 'minDegree' }
  | { kind: 'minAvgZScore' }
  | { kind: 'mentionTypes' }
  | { kind: 'anomalyBuckets' }
  | { kind: 'mutualOnly' };

export interface ActiveFilterChip {
  id: FilterChipId;
  // react key / aria id
  key: string;
  // "Group:" prefix for value chips; absent on on/off chips
  group?: string;
  // every selected value, in display order — the chip shows the first + "+N"
  values: string[];
}

export function buildFilterChips({
  t,
  sections,
  nodeFilters,
  mentionTypes,
  selectedMentionTypes,
  edgeAnomalyOptions,
  hiddenEdgeAnomalyBuckets,
  mutualOnly,
}: {
  t: Translate;
  sections: NodeFilterSection[];
  nodeFilters: NodeFilters;
  mentionTypes: { type: string; count: number }[];
  selectedMentionTypes: string[];
  edgeAnomalyOptions: { bucket: ZScoreBucket; count: number }[];
  hiddenEdgeAnomalyBuckets: ZScoreBucket[];
  mutualOnly: boolean;
}): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  sections.forEach((section) => {
    const selected = nodeFilters[section.key];
    if (selected.length === 0) return;
    // display order follows the section, not click order
    const values = section.options
      .filter((option) => selected.includes(option.value))
      .map((option) => option.label);
    chips.push({
      id: { kind: 'facet', category: section.key },
      key: `facet:${section.key}`,
      group: t(section.title),
      values: values.length > 0 ? values : selected,
    });
  });
  if (nodeFilters.minDegree > 0) {
    chips.push({
      id: { kind: 'minDegree' },
      key: 'minDegree',
      group: t('Minimum connections'),
      values: [`${nodeFilters.minDegree}+`],
    });
  }
  if (nodeFilters.minAvgZScore !== null) {
    chips.push({
      id: { kind: 'minAvgZScore' },
      key: 'minAvgZScore',
      group: t('Minimum avg Z-Score'),
      values: [`≥ ${nodeFilters.minAvgZScore}`],
    });
  }
  if (selectedMentionTypes.length > 0) {
    chips.push({
      id: { kind: 'mentionTypes' },
      key: 'mentionTypes',
      group: t('Interaction types'),
      values: mentionTypes
        .filter(({ type }) => selectedMentionTypes.includes(type))
        .map(({ type }) => mentionTypeLabel(t, type)),
    });
  }
  if (hiddenEdgeAnomalyBuckets.length > 0) {
    const shown = edgeAnomalyOptions
      .filter(({ bucket }) => !hiddenEdgeAnomalyBuckets.includes(bucket))
      .map(({ bucket }) => t(ZSCORE_BUCKET_LABELS[bucket]));
    chips.push({
      id: { kind: 'anomalyBuckets' },
      key: 'anomalyBuckets',
      group: t('Relation anomaly'),
      values: shown.length > 0 ? shown : [t('None')],
    });
  }
  if (mutualOnly) {
    chips.push({
      id: { kind: 'mutualOnly' },
      key: 'mutualOnly',
      values: [t('Mutual connections only')],
    });
  }
  return chips;
}
